import { useState, type ReactNode } from "react";
import type { CombatantId, OptInActionRiderPrompt } from "@lancer-vtt/domain";
import type { ActionTargeting, MechActionRoute, DirectedDeployRequirement } from "@lancer-vtt/rules";
import { InfoTip } from "../../../routes/home/pilotInfoTip";
import { RulesText } from "../../RulesText";
import type { GlossaryKey } from "../../glossary/combatGlossary";
import { Term } from "../../glossary/Term";

/**
 * A presentation-ready view of one unit action — the shape the shared action grid renders, decoupled
 * from either RAW table's type ({@link "@lancer-vtt/rules".NpcAction} / `MechAction`). Each inspector
 * body maps its own RAW slice into these (resolving the tier/base-derived `valueLabel` with its own
 * helper) so the grid stays agnostic to NPC vs mech — the seam that lets one set of components dress
 * both control surfaces.
 */
export type UnitActionView = {
  /** Stable list key + future engine handle (the RAW action id). */
  id: string;
  name: string;
  /**
   * Economy cost class. Two reads here: `"Reaction"` omits the category chip (matching the
   * prototype), and a **Protocol** raises the `PROTOCOL` value badge via {@link protocolBadge} —
   * the QUICK column folds `Quick | Free | Protocol` together, so this is the only thing that tells
   * a Protocol row apart from the free-action row sitting next to it. A granted row *also* renders
   * this string as its own chip, but only while `resolvable` (an advisory one reads `"GM"`).
   */
  cost: string;
  category: string;
  blurb: string;
  /** Whether the action resolves through a live engine path today (only Skirmish, so far). */
  live: boolean;
  /**
   * Whether this action has already been used up this turn — greys out / blocks the row, the SAME affordance-mirrors-the-wall
   * discipline `live`/`resolvable` already follow. Orthogonal to the column's action-point
   * availability (a turn can have points left and still be barred from repeating this one
   * action). `false` off this unit's own turn (nothing to gate against yet).
   */
  usedUp: boolean;
  /** The pre-resolved base-derived value badge text, or null for no badge. */
  valueLabel: string | null;
  /**
   * Core rows only: this row's cost was rewritten to free by a held **cost earmark**
   * (2.1, read off {@link ActionDescriptor.costEarmarked}) — the
   * Everest's Boost under Power Up. Drives the `FREE — …` badge via {@link earmarkedBadge} and
   * corrects the row's own tooltip, which otherwise promises to spend a Quick action it won't.
   * Absent on granted rows: an earmark rewrites the core row by design, never a granted one.
   */
  costEarmarked?: boolean;
  /**
   * A **loadout-granted** action row (unified-action-registry) — enumerated off the
   * mech's frozen `base.affordances` (via `carriedAffordances`), displayed by compendium lookup. When set, the row routes through the
   * granted-activate affordance (resolvable → arm/(target)/fire `request_activate`; advisory → a Scan-like
   * "GM adjudicates" static row) instead of the hardcoded core handlers. Absent on core + NPC rows.
   */
  granted?: boolean;
  /** Granted only: whether an authored overlay body exists (resolvable end-to-end) vs. an advisory row. */
  resolvable?: boolean;
  /**
   * Granted only: every authored clause behind this row is gated out by its own `condition` right
   * now, so firing it would resolve to zero steps (read straight off
   * {@link ActionDescriptor.conditionUnmet}). Blocks the row and retitles it, the same
   * affordance-mirrors-the-wall discipline `usedUp`/`statusBlock` follow — except this one is not a
   * wall at all: the authority ACCEPTS the activate and simply does nothing, so without this the
   * row's click has no observable consequence anywhere for the player to learn from.
   */
  conditionUnmet?: boolean;
  /**
   * Granted only: the UI targeting flow — `"self"` one-click, `"single"` arm-then-click-a-token, `"cell"`
   * arm-then-click-a-cell (Smoke/HEX to a point), `"sites"` a directed multi-pick placement
   * (see {@link grantedTargetingFireable}).
   * `"advisory"` (a `"sites"` carrier's non-literal-count degrade) still renders NON-FIREABLE.
   */
  targeting?: ActionTargeting;
  /** Granted only: the composite overlay action id to activate (`<itemId>:<actionIndex>`). */
  actionId?: string;
  /** Granted only: a Quick Tech action a Full Tech may take as a pick ({@link ActionDescriptor.quickTech}). */
  quickTech?: true;
  /**
   * Granted only: the {@link ActionDescriptor.route} this row enumerated with — `"activate"` for an
   * ordinary granted activation, `"attack"` for a system-granted TECH ATTACK
   * (e.g. SSC Markerlight). `fireGranted` reads this
   * (not `targeting` — both routes can be `"single"`) to arm the tech-attack aim mode instead of
   * the plain activate one; `"reaction"`-routed rows never reach a `UnitActionView` at all (filtered
   * out before this mapping — Category B). Absent on core rows.
   */
  route?: MechActionRoute;
  /**
   * Granted only: the source that granted this action (equipment/talent/frame name) — surfaced in the ⓘ
   * tooltip as "Granted by …", NOT in the row label. Absent when the row `name` already IS the source.
   */
  sourceName?: string;
  /**
   * Present iff `targeting === "sites"` — the directed-deploy pick shape, read straight off
   * {@link ActionDescriptor.sites} (the registry's ONE
   * `directedDeployRequirement` read, never re-derived here). `fireGranted` reads `count`/`size` off
   * THIS field to mint the armed draft (`armDirectedDeployDraft`).
   */
  sites?: DirectedDeployRequirement;
  /**
   * The projected `requiresOptIn` riders on Boost's `on_action_taken` fire — set ONLY by each body's
   * Boost row, off `ProjectedEncounterState.optInActionRiders[c.id]["boost"]`, the same
   * one-row-sets-it-once pattern. `UnitActionRow` renders one checkbox per rider (gated on the row being `interactive`)
   * and resolves the player's explicit toggles onto the `boost` command's `choices` (the only
   * transport for them) via {@link resolveActionRiderChoices}. Absent on every other row — see that
   * function's own doc for why this is Boost-only, not generic over every `on:"action"` carrier.
   */
  optInRiders?: OptInActionRiderPrompt[];
  /** A `DEPLOYED` row's drone (`ActionDescriptor.carrierId`): the click fires the activate at it, no aim. */
  carrierId?: string;
};

/**
 * Fold a held **cost earmark** into a core row's value badge.
 *
 * `ActionDescriptor.cost` reads `"Free"` while an earmark is held, but NO core-row render site reads
 * `cost` at all — the chip renders `category`, the badge renders the value kind — so the Everest's
 * Boost row would look identical with and without Power Up, and its own tooltip would still
 * promised to "spend a Quick action". This is the render site that gap was missing.
 *
 * The badge slot is reused rather than a second one added, matching the shipped precedent directly
 * above — and keeping the value figure,
 * since "how far does this Boost carry me" stays the number the player is actually reading.
 */
export function earmarkedBadge(label: string | null, costEarmarked: boolean): string | null {
  if (!costEarmarked) return label;
  return label ? `FREE — ${label}` : "FREE";
}

/**
 * Is this row's economy {@link UnitActionView.cost} a **Protocol**?
 *
 * Case- and whitespace-insensitive on purpose — the same discipline `grantedIsFull` follows in each
 * inspector body — because the registry mints `cost` from three sources with different casing
 * conventions: `MECH_ACTIONS`/`PILOT_ACTIONS`' literal `"Protocol"`, `lancer-data`'s capitalised
 * `activation` read verbatim, and a pack overlay's lower-case `"protocol"` put through
 * `activationCost`. A render site that compared `=== "Protocol"` would silently stop badging the day
 * a row arrived by the third route.
 */
export function isProtocolCost(cost: string): boolean {
  return cost.trim().toLowerCase() === "protocol";
}

/**
 * Fold a **Protocol** identity into a row's value badge (project `action-ordering-audit` 3.1, D5).
 *
 * **The defect this closes.** The action grid is three columns — QUICK / FULL / REACTION
 * (`grid-template-columns: 1fr 1fr 0.78fr`) — and has no Protocol column, so each inspector body
 * folds `Quick | Free | Protocol` into the QUICK one. A CORE row renders its `category` as its chip
 * (`"SUPPORT"` for both Overcharge and Engage COMP/CON), never its `cost`, so a player reading that
 * column **cannot tell a Protocol row from a free-action row** — the misread that had a Protocol
 * blamed for blocking an ORDNANCE weapon that a free-action grant had actually closed the window on.
 * RAW p.62: *"PROTOCOL: This system can be activated as a free action, but only at the start of the
 * user's turn"* — a real, differently-timed economy class, and the one fact the row was not saying.
 *
 * **A badge in the existing slot, not a fourth column** (D5): a fourth column is a real layout change
 * in an already cramped panel, and the reported confusion was *identity*, not placement. Follows
 * {@link earmarkedBadge} directly above — same badge slot, same `unit-sheet-arow-badge` class, same
 * "prefix, keep the figure the player is reading" shape, so a Protocol row that also carries a value
 * reads `PROTOCOL — SPD 4` rather than losing one of the two.
 *
 * **Applied at the render site, not in the three bodies** — the one deliberate divergence from
 * `earmarkedBadge`'s placement, and the reason is the failure mode this phase was warned about:
 * `granted-action-usage-limits` P3 and `cf502b62` each shipped a correctly-derived descriptor field
 * that NO render site read. `costEarmarked` has to be folded body-side (the mech body composes it
 * with the Skirmish/Backswing override), but `cost` is *already* on every {@link UnitActionView} and
 * already reaches {@link UnitActionRow}, so folding here makes "a body forgot to call it" structurally
 * impossible instead of merely tested-against — and the Mech / Pilot / NPC bodies all inherit it from
 * the one shared row component.
 */
export function protocolBadge(label: string | null, cost: string): string | null {
  if (!isProtocolCost(cost)) return label;
  return label ? `PROTOCOL — ${label}` : "PROTOCOL";
}

/**
 * Which `granted`-row {@link ActionTargeting} kinds can actually FIRE from the browser today — fails
 * CLOSED: any kind not explicitly listed here (a future `ActionTargeting` variant) renders the row
 * non-fireable rather than a live button that would send a `request_activate` the room gate might
 * reject. `"sites"` — the directed multi-pick placement `targetingFromOverlay` mints for a
 * `deploy_object` atom — is fireable: the accumulating board gesture (`activate_sites`'s `ActionDraft`
 * variant, the legal-cell wash, illegal-cell routing) exists. `"advisory"` (a `"sites"` carrier
 * whose `sectionCount`/`size` isn't yet a literal number) stays excluded — there is no known pick count
 * to arm a draft with.
 */
export function grantedTargetingFireable(t: ActionTargeting | undefined): boolean {
  return t === "self" || t === "single" || t === "cell" || t === "sites";
}

/** The glossary term each economy column's header opens (`combat-glossary` 3.3): its action type. */
export const ACTION_COLUMN_TERM: Readonly<Record<string, GlossaryKey>> = {
  QUICK: "type:quick",
  FULL: "type:full",
  REACTION: "type:reaction",
};

/**
 * One **economy column** of a unit action grid
 * — a brutalist header (QUICK / FULL / REACTION) over its actions as {@link UnitActionRow}s. Data-driven
 * from the {@link UnitActionView} slice the parent split by `cost`. The header carries an `availLabel`
 * readout derived from the real `TurnBudget`, and when the column is unavailable (`!available`) its rows
 * dim (`is-unavail`) — the economy folded in, no standalone strip. Shared verbatim by the NPC and mech
 * sheets.
 */
export function UnitActionColumn({
  title,
  tone,
  actions,
  combatantId,
  available,
  availLabel,
  canControl,
  onSkirmish,
  onBoost,
  onBarrage,
  onRam,
  onGrapple,
  onImprovisedAttack,
  onStabilize,
  onQuickTech,
  onFullTech,
  onGrantedActivate,
  onHide,
  onSearch,
  onOverwatch,
  onDisengage,
  onPrepare,
  statusBlockReason,
  topSlot,
}: {
  title: string;
  tone: "quick" | "full" | "react";
  actions: readonly UnitActionView[];
  /**
   * The combatant this column belongs to — folded
   * into each row's React `key` (`` `${combatantId}:${a.id}` ``) rather than the bare `a.id` this
   * used to key with. The three inspector bodies render WITHOUT a `key={c.id}` of their own
   * (`CombatWindows.tsx`'s selection-list render), so switching the selected combatant does NOT
   * remount them, and a bare `a.id` key is stable across two combatants of the same kind — a
   * Boost checkbox's local state would otherwise leak from combatant A's row to combatant B's
   * whenever both project the same rider key (two mechs with the same flight system, e.g.).
   * Combatant-scoping the key forces a remount on every combatant switch, resetting that state
   * structurally instead of via a `useEffect` sync.
   */
  combatantId: CombatantId;
  /**
   * An optional situational control pinned at the TOP of the column, above the action rows:
   * the RELEASE / BREAK GRAPPLE follow-up lives here on the QUICK column
   * while a grapple is live, instead of being buried in the Statuses band. Absent on every other
   * column. The parent owns its grapple-live + ownership gating; this just positions + emphasizes it.
   */
  topSlot?: ReactNode;
  /** Whether this column's economy slot is open right now (off the real TurnBudget) — drives row dimming. */
  available: boolean;
  /** The mono header readout for the slot (`2 LEFT` / `OPEN` / `SPENT` / `IDLE`). */
  availLabel: string;
  canControl: boolean;
  /** Arm the live Skirmish row → the shared ArmedActionPanel. */
  onSkirmish?: () => void;
  /**
   * Dispatch the live Boost row — +Speed of move budget, a
   * Quick action. Takes the resolved opt-in-rider choices (`{}` when the row carries no riders or
   * none deviate from their authored default) —
   * `UnitActionRow` resolves them via {@link resolveActionRiderChoices} before calling this.
   */
  onBoost?: (choices: Record<string, string>) => void;
  /** Arm the live Barrage row → the in-panel BarrageChooser. Mech FULL column only; absent on NPC / non-FULL columns (whose tables carry no Barrage row). */
  onBarrage?: () => void;
  /** Arm the live Ram row → the in-panel RamChooser. Mech and NPC **QUICK** columns (both tables carry a `status: "live"` Ram row and both bodies pass this); absent on non-QUICK columns. */
  onRam?: () => void;
  /** Arm the live Grapple row → the in-panel GrappleChooser. Mech and NPC **QUICK** columns (both tables carry a `status: "live"` Grapple row and both bodies pass this); absent on non-QUICK columns. */
  onGrapple?: () => void;
  /** Arm the live Improvised Attack row → the in-panel ImprovisedAttackChooser (RAW Core p.71, item 2). Mech FULL column only; absent on NPC / non-FULL columns (NPC's tiered flavor stays engine-pending). */
  onImprovisedAttack?: () => void;
  /** Arm the live Stabilize row → the in-panel StabilizeChooser. Mech FULL column only; absent on NPC / non-FULL columns. */
  onStabilize?: () => void;
  /** Arm the live Quick Tech row → the in-panel QuickTechChooser. Mech QUICK column only; absent on NPC / non-QUICK columns. */
  onQuickTech?: () => void;
  /** Arm the live Full Tech row → the in-panel FullTechChooser. Mech FULL column only; absent on NPC / non-FULL columns. */
  onFullTech?: () => void;
  /** Fire/arm a loadout-granted activate row (unified-action-registry); mech QUICK/FULL columns only, absent elsewhere. */
  onGrantedActivate?: (action: UnitActionView) => void;
  /** Fire the live Hide row → one `request_hide` (project P3). Mech QUICK column only; absent on NPC / non-QUICK columns. */
  onHide?: () => void;
  /** Arm the live Search row → the in-panel SearchChooser (project P3). Mech QUICK column only; absent on NPC / non-QUICK columns. */
  onSearch?: () => void;
  /**
   * Arm the live Overwatch row → the SAME SkirmishChooser, tagged `reaction: true` — a GM-declared
   * reaction dispatch, not the passive "prompted when hit" shape Brace uses. NPC REACTION column
   * only; absent elsewhere (a mech's Overwatch stays the Category-B informational row).
   */
  onOverwatch?: () => void;
  /** Fire the live Disengage row → one `request_disengage`. Mech/NPC FULL column only; absent on non-FULL columns. */
  onDisengage?: () => void;
  /**
   * Arm the live Prepare row → the in-panel `PrepareChooser` (RAW Core pp. 71–72,
   * `projects/prepare-hold-browser-arming.md` D1). Mech **and** NPC **QUICK** column only —
   * Prepare is a Quick action and the verbs it can ready are Quick actions, so the FULL and
   * REACTION columns never pass it. Absent on the pilot body too: a pilot's attack is FIGHT, a
   * Full action, so a pilot composer would list no verbs (D3) and that row stays engine-pending.
   */
  onPrepare?: () => void;
  /**
   * The status wall's affordance mirror (1.6) — given a row id, the
   * authority's own rejection message when the actor's statuses forbid it, else `null`. Threaded to
   * every row so a Shut Down / Stunned / Down-and-Out actor's bar greys what the server would refuse
   * (BOOT UP excepted, off the wall's own pinned exception). Both inspector bodies pass it; a column
   * without it simply doesn't mirror. Never re-implement the wall here — see {@link actionStatusBlockReason}.
   */
  statusBlockReason?: (rowId: string) => string | null;
}) {
  return (
    <div className={`unit-sheet-col is-${tone}${available ? "" : " is-unavail"}`}>
      <div className="unit-sheet-col-head">
        <span className="unit-sheet-col-title">{ACTION_COLUMN_TERM[title] ? <Term k={ACTION_COLUMN_TERM[title]}>{title}</Term> : title}</span>
        <span className="unit-sheet-col-avail">{availLabel}</span>
      </div>
      {topSlot}
      {actions.map((a) => (
        <UnitActionRow key={`${combatantId}:${a.id}`} action={a} canControl={canControl} onSkirmish={onSkirmish} onBoost={onBoost} onBarrage={onBarrage} onRam={onRam} onGrapple={onGrapple} onImprovisedAttack={onImprovisedAttack} onStabilize={onStabilize} onQuickTech={onQuickTech} onFullTech={onFullTech} onGrantedActivate={onGrantedActivate} onHide={onHide} onSearch={onSearch} onOverwatch={onOverwatch} onDisengage={onDisengage} onPrepare={onPrepare} statusBlockReason={statusBlockReason} />
      ))}
    </div>
  );
}

/**
 * `UnitActionRow`'s Boost-only opt-in-rider resolution funnel — the action-family analogue of
 * `CombatApp.tsx`'s `resolveSkirmishFireChoices` (see its header comment for the full anti-lying-UI
 * rationale; the engine's `fireTrigger` gate is fail-closed — a missing `optIn:<name>` answer
 * DECLINES). Turns the row's local `deviations` (explicit player
 * toggles only, keyed by rider `key` — never the resolved value, see `UnitActionRow`'s own state doc)
 * into the explicit wire value {@link UnitActionColumn}'s `onBoost` sends:
 *
 * 1. Every rider in `riders` resolves to an EXPLICIT `"true"`/`"false"` — `deviations[r.key] ??
 * String(r.default)`. An untouched default-ON rider must send `"true"`, never an absent key.
 * 2. A deviation key with no corresponding rider is dropped (never surfaces in the output) — stale,
 * either from a previously-selected combatant (the combatant-scoped row `key`, `UnitActionColumn`'s
 * own doc, resets this to `{}` on a switch) or a rider the projection has since stopped enumerating
 * mid-turn.
 * 3. Empty `riders` → `{}`, matching a plain Boost dispatch's `choices` byte-for-byte — the three
 * inspector bodies' `boost` closures only attach `choices` onto the command when this is
 * non-empty, so an ordinary Boost's forward-log entry carries no `choices` key at all.
 *
 * Exported (not inlined in `UnitActionRow`) for the same reason as `resolveSkirmishFireChoices`: no
 * jsdom/headless React renderer in this repo, so this is pinned directly against a constructed
 * `OptInActionRiderPrompt[]` + deviations map rather than rendering the row and driving a click.
 */
export function resolveActionRiderChoices(
  riders: OptInActionRiderPrompt[],
  deviations: Record<string, string>,
): Record<string, string> {
  const resolved: Record<string, string> = {};
  for (const r of riders) {
    resolved[r.key] = deviations[r.key] ?? String(r.default);
  }
  return resolved;
}

/**
 * **Boost with its opt-in riders**, the command menu's Boost card (`token-command-menu` Phase 4): a
 * mech whose Boost carries `requiresOptIn` riders (the Rapid Burst Jump Jet's Fly) picks them here,
 * then BOOST. The same checkboxes {@link UnitActionRow} renders on an NPC's or pilot's Boost row:
 * ticked state is derived from the explicit toggles over each rider's authored default, never
 * seeded, and {@link resolveActionRiderChoices} turns the toggles into the `boost` command's
 * `choices` at dispatch. Per activation only: the toggles clear on BOOST. A dragged Boost's drop card
 * (`BoostDropCard`) renders the same body with its own `go` button and a CANCEL.
 */
export function BoostRiderControls({
  riders,
  blockReason,
  onBoost,
  go = { label: "BOOST", ariaLabel: "fire boost" },
  onCancel,
}: {
  riders: OptInActionRiderPrompt[];
  /** Why Boost can't be taken now (the row's greyed reason), or `undefined` when it can. */
  blockReason: string | undefined;
  onBoost: (choices: Record<string, string>) => void;
  /** The commit button's text and accessible name. */
  go?: { label: string; ariaLabel: string };
  /** Renders a CANCEL beside the commit button. */
  onCancel?: () => void;
}) {
  const [deviations, setDeviations] = useState<Record<string, string>>({});
  return (
    <>
      <div className="skirmish-hud-optin" role="group" aria-label="optional effects">
        {riders.map((r) => {
          const ticked = (deviations[r.key] ?? String(r.default)) === "true";
          return (
            <label key={r.key} className="skirmish-hud-check">
              <input type="checkbox" checked={ticked} aria-label={r.label} onChange={(e) => setDeviations({ ...deviations, [r.key]: e.target.checked ? "true" : "false" })} />
              <span>{r.label.toUpperCase()}</span>
            </label>
          );
        })}
      </div>
      {blockReason && <div className="skirmish-hud-note">{blockReason}</div>}
      <div className="skirmish-hud-foot">
        {onCancel && (
          <button type="button" className="skirmish-hud-add" onClick={onCancel}>
            CANCEL
          </button>
        )}
        <button
          type="button"
          className="skirmish-hud-go"
          aria-label={go.ariaLabel}
          disabled={blockReason !== undefined}
          onClick={() => {
            onBoost(resolveActionRiderChoices(riders, deviations));
            setDeviations({});
          }}
        >
          {go.label}
        </button>
      </div>
    </>
  );
}

/**
 * One **action row** in a unit action grid.
 * Renders the prototype's row shape: name + category chip (omitted in the REACTION column, matching the
 * prototype) + an optional base-derived **value badge** + an **ⓘ** that is an {@link InfoTip} trigger
 * carrying the action's RAW blurb (portal-based so the window's `overflow:auto` host can't clip it).
 * **Skirmish and Boost are the live rows** — a real button (gated on `canControl`), routed by id: Skirmish
 * (`onSkirmish`) arms the shared resolution flow, Boost (`onBoost`)
 * dispatches the `boost` move-budget top-up directly. Every other action is an honest, non-interactive
 * **"engine pending"** reference (its engine lands as the turn engine does). The ⓘ is a *sibling* of the
 * take target (never nested inside the `<button>`) so the focusable tip trigger stays valid + keyboard-reachable.
 *
 * **Boost's opt-in-rider checkboxes** render as a further
 * sibling block, between the take target and the ⓘ, when `action.optInRiders` is non-empty AND the row
 * is `interactive` — never inside the `<button>` (a nested interactive control inside a `<button>` is
 * broken for both mouse and keyboard). Local `deviations` state holds ONLY explicit player toggles
 * (never the resolved value — see the state's own doc below); {@link resolveActionRiderChoices} turns
 * it into the command's `choices` at dispatch time, then it's cleared — no sticky/remembered state
 * across activations, mirroring `resolveSkirmishFireChoices`'s identical call in `CombatApp.tsx`.
 */
function UnitActionRow({
  action: a,
  canControl,
  onSkirmish,
  onBoost,
  onBarrage,
  onRam,
  onGrapple,
  onImprovisedAttack,
  onStabilize,
  onQuickTech,
  onFullTech,
  onGrantedActivate,
  onHide,
  onSearch,
  onOverwatch,
  onDisengage,
  onPrepare,
  statusBlockReason,
}: {
  action: UnitActionView;
  canControl: boolean;
  onSkirmish?: () => void;
  /** See {@link UnitActionColumn}'s `onBoost` doc — takes the resolved opt-in-rider choices. */
  onBoost?: (choices: Record<string, string>) => void;
  /** Arm the live Barrage row (mech FULL column only); absent elsewhere. */
  onBarrage?: () => void;
  /** Arm the live Ram row (the mech and NPC **QUICK** columns — both tables' Ram row is `status: "live"`); absent elsewhere. */
  onRam?: () => void;
  /** Arm the live Grapple row (the mech and NPC **QUICK** columns — both tables' Grapple row is `status: "live"`); absent elsewhere. */
  onGrapple?: () => void;
  /** Arm the live Improvised Attack row (mech FULL column only; RAW Core p.71, item 2); absent elsewhere (NPC's tiered flavor stays engine-pending). */
  onImprovisedAttack?: () => void;
  /** Arm the live Stabilize row (mech FULL column only); absent elsewhere. */
  onStabilize?: () => void;
  /** Arm the live Quick Tech row (mech QUICK column only); absent elsewhere. */
  onQuickTech?: () => void;
  /** Arm the live Full Tech row (mech FULL column only); absent elsewhere. */
  onFullTech?: () => void;
  /** Fire/arm a loadout-granted activate row (mech QUICK/FULL columns only); absent elsewhere. */
  onGrantedActivate?: (action: UnitActionView) => void;
  /** Fire the live Hide row (mech QUICK column only); absent elsewhere. */
  onHide?: () => void;
  /** Arm the live Search row (mech QUICK column only); absent elsewhere. */
  onSearch?: () => void;
  /**
   * Arm the live Overwatch row (NPC REACTION column only) — a
   * GM-declared reaction dispatch, interactive despite `cost === "Reaction"` (unlike the passive
   * Brace row). Absent elsewhere (a mech's Overwatch stays the non-interactive Category-B reference).
   */
  onOverwatch?: () => void;
  /** Fire the live Disengage row (mech/NPC FULL column only); absent elsewhere. */
  onDisengage?: () => void;
  /** Arm the live Prepare row → the in-panel `PrepareChooser` (mech/NPC **QUICK** column only — a Quick action, RAW pp. 71–72); absent elsewhere (the pilot row stays engine-pending, D3). */
  onPrepare?: () => void;
  /** The status wall's affordance mirror — see {@link UnitActionColumn}'s prop of the same name. */
  statusBlockReason?: (rowId: string) => string | null;
}) {
  // Boost's opt-in-rider deviations — ONLY the
  // player's explicit toggles, never the resolved/derived checkbox value. Ticked state is DERIVED
  // at render (`deviations[r.key] ?? String(r.default)`), never seeded with the authored default;
  // toggling always writes an explicit `"true"`/`"false"` string, never deletes the key — absence
  // means "no deviation from the authored default," a DIFFERENT thing from "the player explicitly
  // unticked it." Cleared to `{}` right after a Boost dispatch — no sticky/remembered state
  // across activations; do NOT add localStorage/a ref/any cross-activation carry-over here, the
  // same rule `CombatApp.tsx`'s `resolveSkirmishFireChoices` block follows.
  // Reset on every combatant switch structurally, via the row's OWN combatant-scoped `key` — see
  // {@link UnitActionColumn}'s `combatantId` doc for the state-leak hazard this closes.
  const [deviations, setDeviations] = useState<Record<string, string>>({});
  // The status wall's verdict for THIS row (1.6) — the authority's own
  // message, or `null` when the actor's statuses permit it. Read once and shared by BOTH the granted
  // branch below and the core-row treatment further down, so the two never drift.
  const statusBlock = statusBlockReason?.(a.id) ?? null;
  // A loadout-granted action (unified-action-registry) routes through its own affordance,
  // distinct from the hardcoded core handlers below: a RESOLVABLE one is an activate button (arm/(target)
  // /fire `request_activate` via the host); an ADVISORY one (no authored overlay body) renders Scan-like —
  // a static "GM adjudicates" row that NEVER sends `request_activate` (the resolver throws on it).
  if (a.granted) {
    // `a.usedUp` gates a granted row exactly like the core-row treatment below (`is-used` dims it,
    // `is-static` replaces its `<button>`). Without this read NEITHER the once-per-turn wall NOR a
    // scene-depleted `Limited N` cap would grey a granted row in the browser —
    // the row stayed a live take button until the click round-tripped a server rejection.
    // `grantedTargetingFireable` fails CLOSED (1.7): a `"sites"`/`"advisory"`
    // targeting kind — or any future kind this switch doesn't know about — renders non-fireable rather
    // than a live button that would send a `request_activate` the room gate (task 1.4) now rejects.
    const fireable = grantedTargetingFireable(a.targeting);
    // `statusBlock` (1.6) gates a granted row exactly like `usedUp` does: a granted activation is a
    // `request_activate`, which reaches the room's generic status-legality seam — so on a Stunned /
    // Shut Down / Down-and-Out actor the authority WILL refuse it, and the row must say so up front.
    // `a.conditionUnmet` joins the same gate list. Unlike its three neighbours it mirrors no wall —
    // the room ACCEPTS a conditioned activate and resolves it to zero steps — so without this gate
    // the Everest's Power-Up-gated free Boost would render a live button whose click produces no
    // rejection, no toast and no state change.
    const canFire =
      !!a.resolvable && canControl && !!onGrantedActivate && !a.usedUp && !a.conditionUnmet && fireable && statusBlock === null;
    const title = a.resolvable
      ? canControl
        ? statusBlock
          ? `${a.name} — ${statusBlock}`
          : a.conditionUnmet
          ? `${a.name} — not available yet (its trigger condition isn't met)`
          : a.usedUp
          ? `${a.name} — no uses remaining (once per turn, or out of charges for the scene)`
          : !fireable
            ? `${a.name} — ${a.cost}: not yet fireable from the browser (CLI-only for now)`
            : a.targeting === "cell"
              ? `${a.name} — ${a.cost}: arm, then click a target cell`
              : a.targeting === "single"
                ? `${a.name} — ${a.cost}: arm, then click a target token`
                : `${a.name} — ${a.cost}: activate on self`
        : `${a.name} — read-only (you don't command this unit)`
      : `${a.name} — ${a.cost}: GM adjudicates (no engine body — resolved manually)`;
    // The granted twin of the core row's PROTOCOL badge (3.1/D5) — needed on exactly the ADVISORY
    // half. A `resolvable` granted row already renders `cost.toUpperCase()` as its chip, so it says
    // "PROTOCOL" on its own and a badge would only double it; an advisory row's chip is overwritten
    // with `"GM"` (the "GM adjudicates, no engine body" tell), which drops the economy class
    // entirely — a Protocol system with no authored overlay is indistinguishable from a Quick one.
    // Same slot, same class, same helper as the core branch: one definition of the badge, two chips.
    const grantedBadge = a.resolvable ? null : protocolBadge(a.valueLabel, a.cost);
    const grantedBody = (
      <>
        <span className="unit-sheet-arow-name">{a.name}</span>
        <span className="unit-sheet-arow-spacer" />
        <span className="unit-sheet-arow-cat">{a.resolvable ? a.cost.toUpperCase() : "GM"}</span>
        {grantedBadge && <span className="unit-sheet-arow-badge">{grantedBadge}</span>}
      </>
    );
    return (
      <div
        className={`unit-sheet-arow is-${a.resolvable ? (canControl ? "live" : "watch") : "pending"}${a.usedUp || a.conditionUnmet ? " is-used" : ""}`}
      >
        {canFire ? (
          <button type="button" className="unit-sheet-arow-take" title={title} onClick={() => onGrantedActivate!(a)}>
            {grantedBody}
          </button>
        ) : (
          <div className="unit-sheet-arow-take is-static" title={title}>
            {grantedBody}
          </div>
        )}
        <InfoTip
          className="unit-sheet-arow-tip"
          trigger={
            <span className="unit-sheet-arow-info" aria-hidden>
              ⓘ
            </span>
          }
        >
          <UnitActionTipBody name={a.name} category={a.resolvable ? a.cost : "GM adjudicates"} blurb={a.blurb} source={a.sourceName} />
        </InfoTip>
      </div>
    );
  }

  const live = a.live;
  const isReaction = a.cost === "Reaction";
  const isOverwatch = a.id === "overwatch";
  // A live REACTION (Brace) is **passively fired** — the engine's built-in
  // reaction registry prompts the defender via the cross-actor PromptModal when they're hit, so there is
  // no proactive "take" button (clicking would have fallen through to `onSkirmish`, arming a skirmish —
  // wrong). The row is read-only/informational; its availability rides the column's REACTION avail label.
  // **Overwatch is the proactively-declared exception**: a
  // GM-DECLARED reaction dispatch (arm → click a target → fire NOW with `reaction: true`), so it carves
  // out of the passive-Reaction treatment below exactly like Overcharge carves out of the Quick column's
  // dimming — interactive whenever the host wired `onOverwatch` (mech Overwatch stays informational,
  // since the mech column never passes it).
  // The live rows take DIFFERENT actions, routed by id so each reaches its own handler (Skirmish arms
  // the single-weapon aim; Boost dispatches the budget top-up directly; Barrage arms the in-panel
  // two-weapon chooser; Ram arms the in-panel melee-target + push chooser; Stabilize arms the in-panel
  // primary+secondary self-maintenance chooser; Quick/Full Tech arm the in-panel tech-option choosers).
  //
  // **The chain terminates at Skirmish's OWN rung, never at a bare `onSkirmish` fallback**
  // (1.4 — the root-cause hardening). It used to end `: onSkirmish`, so ANY
  // live-but-unrouted row silently armed a single-weapon attack instead of doing what it said: a player
  // clicked SHUT DOWN and the board armed a Skirmish. That is a *correctness* bug, not missing UI, and
  // it would recur for every action added to a core table. Now an unrouted row yields `undefined`, which
  // `interactive` folds in below (`!!onTake`) — it renders as an honest static "not yet wired in the
  // browser" row rather than a live button wired to the wrong action. `skirmish` itself is the
  // fallback's rightful owner and keeps it via `isSkirmish`. The reaction carve-out that motivated this
  // (Brace, above) is now the general case rather than a one-off.
  const isSkirmish = a.id === "skirmish";
  const isBoost = a.id === "boost";
  const isBarrage = a.id === "barrage";
  const isRam = a.id === "ram";
  const isGrapple = a.id === "grapple";
  const isImprovisedAttack = a.id === "improvised_attack";
  const isStabilize = a.id === "stabilize";
  const isQuickTech = a.id === "quick_tech";
  const isFullTech = a.id === "full_tech";
  const isHide = a.id === "hide";
  const isSearch = a.id === "search";
  const isDisengage = a.id === "disengage";
  // Prepare (RAW pp. 71-72) — a Quick action on BOTH the mech and NPC tables, so (like Shut Down and
  // Self-Destruct) it only ever finds its handler on a QUICK column; arms the in-panel
  // `PrepareChooser` — Stabilize/Self-Destruct's form-plus-commit-button shape, not a board-click
  // shape, because the readied verb, its weapon and the trigger are all collected up front and the
  // hold's eventual TARGET isn't knowable until release. The pilot table's Prepare row stays
  // `engine-pending` (`projects/prepare-hold-browser-arming.md` D3), so it never reaches here.
  const isPrepare = a.id === "prepare";
  // Boost's `onClick={onTake}` must stay a bare ` => void` like every other row's — `onBoost`
  // itself now takes the resolved choices, so this
  // wraps the resolve→dispatch→clear sequence rather than assigning `onBoost` directly (which
  // would leave `onClick` handing it the click's MouseEvent as its `choices` argument).
  const onTake = isBoost && onBoost
    ? () => {
        onBoost(resolveActionRiderChoices(a.optInRiders ?? [], deviations));
        setDeviations({});
      }
    : isBarrage && onBarrage
      ? onBarrage
      : isRam && onRam
        ? onRam
        : isGrapple && onGrapple
          ? onGrapple
          : isImprovisedAttack && onImprovisedAttack
            ? onImprovisedAttack
            : isStabilize && onStabilize
              ? onStabilize
                : isQuickTech && onQuickTech
                  ? onQuickTech
                  : isFullTech && onFullTech
                    ? onFullTech
                    : isHide && onHide
                      ? onHide
                      : isSearch && onSearch
                        ? onSearch
                        : isOverwatch && onOverwatch
                          ? onOverwatch
                          : isDisengage && onDisengage
                            ? onDisengage
                                    : isPrepare && onPrepare
                                      ? onPrepare
                                                      : isSkirmish
                                                        ? onSkirmish
                                                        : undefined;

  // The once-per-turn ledger gate —
  // greys out / blocks a row already used this turn, mirroring the column's action-point
  // dimming but as its own, orthogonal wall (a turn can have points left and still be barred
  // from repeating this one action). Never blocks a reaction (Brace is already excluded above).
  // Two further conjuncts, both walls this row must not out-offer:
  //   - `!!onTake` (1.4) — a live row this host wired no handler for is INERT, not a mis-routed
  //     Skirmish. Must stay downstream of the `onTake` chain above, hence this ordering.
  //   - `statusBlock === null` (1.6) — the status wall's affordance mirror. A Shut Down (⇒ Stunned),
  //     Stunned or Down-and-Out actor greys every row the authority would refuse; BOOT UP survives on
  //     the wall's own pinned `act_boot_up` exception, not on a second list kept here.
  const interactive =
    live &&
    canControl &&
    !a.usedUp &&
    !!onTake &&
    statusBlock === null &&
    (!isReaction || (isOverwatch && !!onOverwatch));

  // The name + category chip + value badge — shared by the live button and the pending div.
  // The badge folds the row's PROTOCOL identity in (3.1/D5, {@link protocolBadge}): a core row's
  // chip renders `category` (Engage COMP/CON reads "SUPPORT", exactly like Overcharge does), so
  // without this the QUICK column says nothing about which of its rows is start-of-turn-only.
  const coreBadge = protocolBadge(a.valueLabel, a.cost);
  const body = (
    <>
      <span className="unit-sheet-arow-name">{a.name}</span>
      <span className="unit-sheet-arow-spacer" />
      {!isReaction && <span className="unit-sheet-arow-cat">{a.category}</span>}
      {coreBadge && <span className="unit-sheet-arow-badge">{coreBadge}</span>}
    </>
  );

  const takeTitle = interactive
    ? isBoost
      ? a.costEarmarked
        ? // A held earmark pays for this one, so the row must stop promising a spend
          // it won't make — the Everest under Power Up Boosts for free, once per turn.
          `${a.name} — FREE this turn (a granted free use pays for it): +Speed of movement, no Quick action spent`
        : `${a.name} — spend a Quick action for +Speed of movement this turn`
      : isBarrage && onBarrage
        ? `${a.name} — arm a Full attack: pick up to two weapons + targets, then Fire`
        : isRam && onRam
          ? `${a.name} — arm a melee Ram: pick an adjacent target your Size or smaller, optionally knock it back 1 space`
          : isGrapple && onGrapple
            ? `${a.name} — arm a melee Grapple: pick an adjacent target — on a hit, both become bound (Engaged + Grappled)`
            : isImprovisedAttack && onImprovisedAttack
              ? `${a.name} — arm a melee Improvised Attack: pick an adjacent target — on a hit, 1d6 kinetic damage (no weapon needed)`
              : isStabilize && onStabilize
              ? `${a.name} — arm self-maintenance: pick a primary (Cool / Patch) + a secondary, then Stabilize`
                : isQuickTech && onQuickTech
                  ? `${a.name} — arm a tech action: pick Bolster / Invade / Lock On / Scan, then a target`
                  : isFullTech && onFullTech
                    ? `${a.name} — arm a Full Tech: pick TWO Quick Tech options + targets (may repeat the same one), then Fire`
                    : isHide && onHide
                      ? `${a.name} — become Hidden (rejected if any hostile has a clear line to you)`
                      : isSearch && onSearch
                        ? `${a.name} — arm a target pick (Hidden + in-Sensors-range only), then SEARCH`
                        : isOverwatch && onOverwatch
                          ? `${a.name} — GM-declared: arm a weapon + click a target now, firing as a reaction (spends its reaction)`
                          : isDisengage && onDisengage
                            ? `${a.name} — ignore Engagement and reactions from your movement until the end of this turn`
                                    : isPrepare && onPrepare
                                      ? `${a.name} — arm a hold: pick a quick action to ready, its weapon, and a "when X, then Y" trigger, then PREPARE`
                                                        : `${a.name} — arm a single-weapon attack, then click a target token`
    : // The status wall's own message first (1.6) — a greyed row must say WHY, or a GM reads it as the
      // app ignoring them rather than as the rules holding.
      statusBlock && live && canControl
      ? `${a.name} — ${statusBlock}`
      : live && a.usedUp && canControl && !isReaction
      ? `${a.name} — already used this turn (once per turn)`
      : live && isReaction
        ? `${a.name} — a reaction; you're prompted to use it when you're hit (1/round)`
        : // A live row this host wired no handler for (1.4's hardening) — honest about being inert
          // instead of silently arming a Skirmish, and distinct from the "you don't command this unit"
          // read-only case below, which is about ownership rather than wiring.
          live && canControl && !onTake
          ? `${a.name} — not yet wired in the browser (CLI-only for now)`
          : live
          ? `${a.name} — read-only (you don't command this unit)`
          : `${a.name} — resolves manually (engine pending)`;

  return (
    <div
      className={`unit-sheet-arow is-${live ? (canControl ? "live" : "watch") : "pending"}${a.usedUp ? " is-used" : ""}`}
    >
      {interactive ? (
        <button type="button" className="unit-sheet-arow-take" title={takeTitle} onClick={onTake}>
          {body}
        </button>
      ) : (
        <div className="unit-sheet-arow-take is-static" title={takeTitle}>
          {body}
        </div>
      )}
      {/* Boost's opt-in-rider checkboxes — a SIBLING of
          the take target above, never nested inside its `<button>` (a nested interactive control
          inside a `<button>` is broken for both mouse and keyboard). Renders only when riders are
          projected AND the row is interactive — a greyed/static/read-only row must not offer a live
          checkbox. Ticked state is derived, never seeded (see the `deviations` state doc above);
          `aria-label={r.label}` gives each input a stable, testable handle for the browser tier. */}
      {interactive && a.optInRiders && a.optInRiders.length > 0 && (
        <div className="unit-sheet-arow-optin" role="group" aria-label="optional effects">
          {a.optInRiders.map((r) => {
            const ticked = (deviations[r.key] ?? String(r.default)) === "true";
            return (
              <label key={r.key} className="unit-sheet-arow-optin-check">
                <input
                  type="checkbox"
                  checked={ticked}
                  aria-label={r.label}
                  onChange={(e) => setDeviations({ ...deviations, [r.key]: e.target.checked ? "true" : "false" })}
                />
                <span>{r.label}</span>
              </label>
            );
          })}
        </div>
      )}
      <InfoTip
        className="unit-sheet-arow-tip"
        trigger={
          <span className="unit-sheet-arow-info" aria-hidden>
            ⓘ
          </span>
        }
      >
        <UnitActionTipBody name={a.name} category={a.category} blurb={a.blurb} />
      </InfoTip>
    </div>
  );
}

/**
 * Tooltip body for a unit action — name + category chip + the RAW blurb, reusing the shared `.be-tip-*`
 * panel structure ({@link InfoTip} portals it to `document.body`). The category is shown here even for
 * REACTION rows (where the in-row chip is omitted to match the prototype).
 */
function UnitActionTipBody({ name, category, blurb, source }: { name: string; category: string; blurb: string; source?: string }) {
  return (
    <>
      <div className="be-tip-head">
        <span className="be-tip-title">{name}</span>
        <span className="be-tip-tag">{category}</span>
      </div>
      {source && <p className="be-tip-detail">Granted by {source}</p>}
      {/* Granted-action blurbs are the compendium's own `detail`/talent-rank prose (actionRegistry),
          which is an HTML fragment — as a bare React text child a `<br>` printed on screen verbatim. */}
      <RulesText className="be-tip-desc" html={blurb} />
    </>
  );
}
